import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type {
  BugReport,
  ConvertBugReportToQuestRequest,
  PaginatedResponse,
} from '@aegislab/shared';
import { bugReportApi, bugReportKeys } from '@/entities/bug-report';
import { questKeys } from '@/shared/queryKeys';
import type {
  CreateBugReportInput,
  UpdateBugReportInput,
  EditBugReportInput,
} from '@/entities/bug-report';

const LIST_PAGE_LIMIT = 100;

export function useBugReportHotspots() {
  return useQuery({
    queryKey: bugReportKeys.hotspots(),
    queryFn: () => bugReportApi.fetchHotspots(),
    staleTime: 60_000,
  });
}

export function useBugReportMonthlyStats(months: number, enabled: boolean) {
  return useQuery({
    queryKey: bugReportKeys.monthlyStats(months),
    queryFn: () => bugReportApi.fetchBugReportMonthlyStats(months),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useBugReportProcessorStats(months: number, enabled: boolean) {
  return useQuery({
    queryKey: bugReportKeys.processorStats(months),
    queryFn: () => bugReportApi.fetchBugReportProcessorStats(months),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useBugReports() {
  return useInfiniteQuery<PaginatedResponse<BugReport>>({
    queryKey: bugReportKeys.list(),
    queryFn: ({ pageParam }) =>
      bugReportApi.fetchBugReports({
        limit: LIST_PAGE_LIMIT,
        cursor: pageParam as string | undefined,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useBugReport(id: string) {
  return useQuery({
    queryKey: bugReportKeys.detail(id),
    queryFn: () => bugReportApi.fetchBugReport(id),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useBugReportEvents(id: string) {
  return useQuery({
    queryKey: bugReportKeys.events(id),
    queryFn: () => bugReportApi.fetchBugReportEvents(id),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useBugReportSuggestions(pageContext: string, enabled?: boolean) {
  return useQuery({
    queryKey: bugReportKeys.suggestions(pageContext),
    queryFn: () => bugReportApi.fetchBugReportSuggestions(pageContext),
    enabled: enabled ?? !!pageContext,
    staleTime: 30_000,
  });
}

export function useCreateBugReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateBugReportInput) => bugReportApi.createBugReport(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

export function useUpdateBugReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBugReportInput }) =>
      bugReportApi.updateBugReport(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

/**
 * 제보 → 퀘스트 전환. 성공하면 제보(questId 링크)와 퀘스트 목록이 둘 다 바뀌므로
 * 양쪽 캐시를 무효화한다 — 제보만 무효화하면 새로 발행된 퀘스트가 목록에 안 뜬다.
 */
export function useConvertBugReportToQuest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ConvertBugReportToQuestRequest }) =>
      bugReportApi.convertBugReportToQuest(id, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
      void queryClient.invalidateQueries({ queryKey: questKeys.all });
    },
  });
}

export function useAddAffectedUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bugReportApi.addAffectedUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

export function useVerifyResolution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, verified }: { id: string; verified: boolean }) =>
      bugReportApi.verifyResolution(id, verified),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

export function useEditBugReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: EditBugReportInput }) =>
      bugReportApi.editBugReport(id, data),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.detail(variables.id) });
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

export function useCreateBugReportEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      bugReportApi.createBugReportEvent(id, content),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.events(variables.id) });
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.detail(variables.id) });
    },
  });
}

// ── Votes (기능요청 지지 투표) ──
// 키가 팩토리 루트 안이라 기존 무효화(useCreate/Update 등)가 자동 커버. `votes` 는 `detail`
// 하위라 상세 무효화에도 함께 걸린다.

export function useBugReportVotes(id: string, enabled = true) {
  return useQuery({
    queryKey: bugReportKeys.votes(id),
    queryFn: () => bugReportApi.fetchBugReportVotes(id),
    enabled: !!id && enabled,
    staleTime: 30_000,
    // BE 미배포 시 404 를 빠르게 확정 — 방어적 폴백(D8). 섹션은 data 없으면 감춘다.
    retry: false,
  });
}

export function useCastVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bugReportApi.castBugReportVote(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}

export function useRetractVote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => bugReportApi.retractBugReportVote(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: bugReportKeys.all });
    },
  });
}
